"use client";

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { pipefyApi } from "@/lib/api-client";
import {
  DEFAULT_TIPOS_DATABASE_ID,
  N3_CATALOG_DATABASE_DEFAULT_NAME,
  N3_CATALOG_DATABASE_NAME_PREFIX,
  createN3CatalogPipefyDatabase,
  loadN3CatalogConfigFromDb,
  loadN3CatalogConfigLocal,
  saveN3CatalogConfigLocal,
  saveN3CatalogIdsToDb,
  filterN3RowsForPipefyCatalog,
  loadNonzeroN3ChavesOrcamento,
  sourceViewName,
  type N3CatalogConfig,
  type N3CatalogPreview,
  type N3CatalogSyncResult,
} from "@/lib/n3GlobalCatalog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Database, AlertTriangle, CheckCircle2, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function CatalogoN3GlobalTab() {
  const [config, setConfig] = useState<N3CatalogConfig>(() => loadN3CatalogConfigLocal());
  const [configLoaded, setConfigLoaded] = useState(false);
  const [tableMissing, setTableMissing] = useState(false);
  const [preview, setPreview] = useState<N3CatalogPreview | null>(null);
  const [verifyResult, setVerifyResult] = useState<Record<string, unknown> | null>(null);
  const [syncResult, setSyncResult] = useState<N3CatalogSyncResult | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingSync, setLoadingSync] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingCreateDb, setLoadingCreateDb] = useState(false);
  const [savingIds, setSavingIds] = useState(false);
  const [newDbName, setNewDbName] = useState(() => N3_CATALOG_DATABASE_DEFAULT_NAME);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { config: fromDb, tableMissing: missing } = await loadN3CatalogConfigFromDb();
        if (!cancelled) {
          setConfig(fromDb);
          setTableMissing(missing);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : "Erro ao carregar config do Supabase");
        }
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!configLoaded) return;
    saveN3CatalogConfigLocal(config);
  }, [config, configLoaded]);

  const fetchPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const view = sourceViewName(config.sourceMode);
      const { data: viewRows, error: viewErr } = await supabase
        .from(view)
        .select("chave_externa, nome_tipo")
        .eq("ano", config.ano)
        .eq("nivel", "N3")
        .eq("ativo_no_ano", true);
      if (viewErr) throw viewErr;

      const bruto = viewRows || [];
      const nonzeroKeys =
        config.sourceMode === "orcamento"
          ? await loadNonzeroN3ChavesOrcamento(config.ano)
          : null;
      const { rows: elegiveis, excluidosResultadoFinanceiro, excluidosValorZerado } =
        filterN3RowsForPipefyCatalog(bruto, {
          sourceMode: config.sourceMode,
          nonzeroOrcamentoKeys: nonzeroKeys,
        });

      const { data: mapRows, error: mapErr } = await supabase
        .from("solis_tipos_despesa_pipefy_map")
        .select("chave_externa, ativo, pipefy_database_id")
        .eq("ano", config.ano)
        .eq("nivel", "N3");
      if (mapErr) throw mapErr;

      const mapKeys = new Set(
        (mapRows || []).filter((m) => m.pipefy_database_id).map((m) => m.chave_externa as string)
      );
      const ativos = (mapRows || []).filter((m) => m.ativo !== false && m.pipefy_database_id).length;
      const inativos = (mapRows || []).filter((m) => m.ativo === false).length;

      const semMapa = elegiveis.filter((r) => !mapKeys.has(r.chave_externa as string));

      setPreview({
        ano: config.ano,
        sourceMode: config.sourceMode,
        noOrcadoBruto: bruto.length,
        noOrcado: elegiveis.length,
        excluidosResultadoFinanceiro,
        excluidosValorZerado,
        noMapaAtivo: ativos,
        noMapaInativo: inativos,
        semRegistroNoMapa: semMapa.length,
        amostraSemMapa: semMapa.slice(0, 8).map((r) => ({
          chave_externa: r.chave_externa as string,
          nome_tipo: r.nome_tipo as string,
        })),
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro na prévia";
      toast.error(msg);
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [config.ano, config.sourceMode]);

  useEffect(() => {
    if (!configLoaded) return;
    fetchPreview();
  }, [fetchPreview, configLoaded]);

  const validateConfig = () => {
    if (!config.n3CatalogTableId.trim()) {
      toast.error("Informe o ID do database catálogo N3 no Pipefy");
      return false;
    }
    if (!config.tiposTableId.trim()) {
      toast.error("Informe o ID do database de tipos");
      return false;
    }
    return true;
  };

  const handleSaveIds = async () => {
    setSavingIds(true);
    try {
      await saveN3CatalogIdsToDb({
        n3CatalogTableId: config.n3CatalogTableId,
        n3CatalogTableName: config.n3CatalogTableName,
        tiposTableId: config.tiposTableId,
      });
      setTableMissing(false);
      toast.success("IDs salvos no Supabase");
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar IDs");
    } finally {
      setSavingIds(false);
    }
  };

  const handleCreateDatabase = async () => {
    const tiposId = config.tiposTableId.trim() || DEFAULT_TIPOS_DATABASE_ID;
    const name = newDbName.trim() || N3_CATALOG_DATABASE_DEFAULT_NAME;
    setLoadingCreateDb(true);
    try {
      const { tableId, tableName } = await createN3CatalogPipefyDatabase({
        name,
        description:
          "Catálogo N3 único para toda a empresa (todos os anos). Sem centro de custo. Sincronize por ano de referência do orçado.",
        tiposTableId: tiposId,
      });
      const next: N3CatalogConfig = {
        ...config,
        n3CatalogTableId: tableId,
        n3CatalogTableName: tableName,
        tiposTableId: tiposId,
      };
      setConfig(next);
      await saveN3CatalogIdsToDb({
        n3CatalogTableId: tableId,
        n3CatalogTableName: tableName,
        tiposTableId: tiposId,
      });
      setTableMissing(false);
      toast.success(`Database criado: ${tableName} (${tableId})`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Erro ao criar database");
    } finally {
      setLoadingCreateDb(false);
    }
  };

  const handleSync = async () => {
    if (!validateConfig()) return;
    setLoadingSync(true);
    setSyncResult(null);
    try {
      const data = (await pipefyApi.syncN3Global({
        n3CatalogTableId: config.n3CatalogTableId.trim(),
        tiposTableId: config.tiposTableId.trim(),
        ano: config.ano,
        allowCreate: config.allowCreate,
        allowDeactivate: config.allowDeactivate,
        sourceMode: config.sourceMode,
      })) as N3CatalogSyncResult;
      setSyncResult(data);
      if (data.success) {
        const concl = data.deactivated ?? 0;
        toast.success(
          `Catálogo sincronizado (${config.ano}): ${data.created ?? 0} criados, ${data.updated ?? 0} atualizados` +
            (concl > 0 ? `, ${concl} concluídos` : "")
        );
        await fetchPreview();
      } else {
        toast.error(data.error || "Falha na sincronização");
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro ao sincronizar";
      if (msg.includes("Failed to fetch") || msg.includes("404")) {
        toast.error("Edge function sync-n3-global-pipefy indisponível. Faça deploy da função no Supabase.");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoadingSync(false);
    }
  };

  const handleVerify = async () => {
    if (!validateConfig()) return;
    setLoadingVerify(true);
    setVerifyResult(null);
    try {
      const data = await pipefyApi.verifyN3Global({
        n3CatalogTableId: config.n3CatalogTableId.trim(),
        tiposTableId: config.tiposTableId.trim(),
        ano: config.ano,
        sourceMode: config.sourceMode,
      });
      setVerifyResult(data);
      toast.success("Verificação concluída");
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Erro ao verificar");
    } finally {
      setLoadingVerify(false);
    }
  };

  return (
    <div className="space-y-4">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Fluxo novo — legado continua ativo</AlertTitle>
        <AlertDescription>
          Um único database Pipefy serve <strong>todos os anos</strong>. O campo &quot;ano&quot; abaixo é só a
          referência do orçado na sincronização (criar novos N3, atualizar e concluir os que saíram do orçado
          — mesma ideia da aba Subtipos / Sincronizar tipos). A aba <strong>Subtipos (legado)</strong> não foi
          alterada.
        </AlertDescription>
      </Alert>

      {tableMissing ? (
        <Alert variant="destructive">
          <AlertTitle>Migração pendente</AlertTitle>
          <AlertDescription>
            A tabela <code>solis_pipefy_catalogo_n3_config</code> ainda não existe. Aplique a migração{" "}
            <code>096_solis_pipefy_catalogo_n3_config.sql</code> no Supabase para persistir os IDs.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Database className="h-5 w-5" />
            Configuração
          </CardTitle>
          <CardDescription>
            Database único e permanente no Pipefy. Campos: Nome, Chave externa, conectores para N3, N2 e N1 no
            database global de tipos — sem Centro de Custo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
            <p className="text-sm font-medium">Criar database no Pipefy</p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="n3-new-db-name">Nome do database</Label>
                <Input
                  id="n3-new-db-name"
                  value={newDbName}
                  onChange={(e) => setNewDbName(e.target.value)}
                  placeholder={N3_CATALOG_DATABASE_DEFAULT_NAME}
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCreateDatabase}
                disabled={loadingCreateDb || !configLoaded}
              >
                {loadingCreateDb ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Criando…
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Criar database catálogo N3
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Cria a tabela e os campos automaticamente; o ID é preenchido abaixo e salvo no Supabase.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="n3-catalog-table-id">ID database catálogo N3 (Pipefy)</Label>
              <Input
                id="n3-catalog-table-id"
                placeholder="ID da tabela — catálogo só N3"
                value={config.n3CatalogTableId}
                onChange={(e) => setConfig((c) => ({ ...c, n3CatalogTableId: e.target.value }))}
              />
              {config.n3CatalogTableName ? (
                <p className="text-xs text-muted-foreground">{config.n3CatalogTableName}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="tipos-table-id">ID database tipos N1/N2/N3 (global)</Label>
              <Input
                id="tipos-table-id"
                placeholder={DEFAULT_TIPOS_DATABASE_ID}
                value={config.tiposTableId}
                onChange={(e) => setConfig((c) => ({ ...c, tiposTableId: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="n3-ano">Ano de referência do orçado</Label>
              <Input
                id="n3-ano"
                type="number"
                value={config.ano}
                onChange={(e) => setConfig((c) => ({ ...c, ano: Number(e.target.value) || c.ano }))}
              />
              <p className="text-xs text-muted-foreground">
                O database no Pipefy é o mesmo em todos os anos. Na virada, sincronize com o novo ano para concluir
                N3 que saíram do orçado.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Origem dos N3</Label>
              <Select
                value={config.sourceMode}
                onValueChange={(v) =>
                  setConfig((c) => ({
                    ...c,
                    sourceMode: v as N3CatalogConfig["sourceMode"],
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="orcamento">Orçamento (planilha)</SelectItem>
                  <SelectItem value="realizado_sem_orcamento">Realizado sem orçamento</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Switch
                id="allow-create-n3"
                checked={config.allowCreate}
                onCheckedChange={(v) => setConfig((c) => ({ ...c, allowCreate: v }))}
              />
              <Label htmlFor="allow-create-n3" className="font-normal cursor-pointer">
                Criar registros faltantes no Pipefy
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="allow-deactivate-n3"
                checked={config.allowDeactivate}
                onCheckedChange={(v) => setConfig((c) => ({ ...c, allowDeactivate: v }))}
              />
              <Label htmlFor="allow-deactivate-n3" className="font-normal cursor-pointer">
                Concluir no Pipefy registros que não estão no orçado de {config.ano}
              </Label>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveIds}
              disabled={savingIds || tableMissing}
            >
              {savingIds ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando…
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Salvar IDs no Supabase
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Antes do primeiro sync aqui, rode{" "}
            <Link to="/dashboard/orcamento/sincronizar" className="text-primary underline">
              Orçamento → Sincronizar tipos
            </Link>{" "}
            para o mesmo ano (N3 precisam existir em <code>solis_tipos_despesa_pipefy_map</code>).
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Prévia ({config.ano})</CardTitle>
            <CardDescription>
              {sourceViewName(config.sourceMode)} — elegíveis: sem Resultado Financeiro e com valor ≠ 0 no orçado
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchPreview} disabled={loadingPreview}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loadingPreview ? "animate-spin" : ""}`} />
            Atualizar prévia
          </Button>
        </CardHeader>
        <CardContent>
          {loadingPreview && !preview ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
            </p>
          ) : preview ? (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <PreviewKpi label="N3 elegíveis (Pipefy)" value={preview.noOrcado} />
                <PreviewKpi label="N3 no mapa (ativo)" value={preview.noMapaAtivo} variant="ok" />
                <PreviewKpi label="Sem registro no mapa" value={preview.semRegistroNoMapa} variant="warn" />
                <PreviewKpi label="N3 inativos no mapa" value={preview.noMapaInativo} />
              </div>
              {(preview.excluidosResultadoFinanceiro > 0 || preview.excluidosValorZerado > 0) && (
                <p className="text-xs text-muted-foreground">
                  Excluídos do catálogo: {preview.excluidosResultadoFinanceiro} resultado financeiro,{" "}
                  {preview.excluidosValorZerado} com valor zerado ({preview.noOrcadoBruto} na view bruta).
                </p>
              )}
              {preview.semRegistroNoMapa > 0 ? (
                <div className="rounded-md border p-3 text-xs space-y-1">
                  <p className="font-medium">Amostra sem mapa (sincronize tipos antes):</p>
                  <ul className="list-disc pl-4 text-muted-foreground">
                    {preview.amostraSemMapa.map((s) => (
                      <li key={s.chave_externa}>
                        {s.nome_tipo}{" "}
                        <span className="font-mono text-[10px]">{s.chave_externa}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Todos os N3 do orçado têm registro no mapa global de tipos.
                </p>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sincronizar catálogo</CardTitle>
          <CardDescription>
            Alimenta só N3 elegíveis (sem Resultado Financeiro, valor ≠ 0 no orçado do ano). Cria, atualiza e
            conclui ausentes — como Sincronizar tipos / subtipos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleSync} disabled={loadingSync || loadingVerify}>
              {loadingSync ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sincronizando…
                </>
              ) : (
                "Sincronizar catálogo N3"
              )}
            </Button>
            <Button variant="outline" onClick={handleVerify} disabled={loadingSync || loadingVerify}>
              {loadingVerify ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verificando…
                </>
              ) : (
                "Verificar (Pipefy vs orçado)"
              )}
            </Button>
          </div>

          {syncResult ? <ResultPanel title="Último sync" data={syncResult} /> : null}
          {verifyResult ? <ResultPanel title="Última verificação" data={verifyResult} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}

function PreviewKpi({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant?: "ok" | "warn";
}) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      {variant === "warn" && value > 0 ? (
        <Badge variant="secondary" className="mt-1 text-[10px]">
          Atenção
        </Badge>
      ) : null}
    </div>
  );
}

function ResultPanel({ title, data }: { title: string; data: object }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <p className="text-sm font-medium mb-2">{title}</p>
      <pre className="text-xs overflow-auto max-h-48 whitespace-pre-wrap">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}
